import { useCallback, useEffect, useState } from 'react'
import { getDefaultPrinter, listPrinters, setDefaultPrinter } from './labelPrintingApi'

export const LAST_PRINTER_KEY = 'mic.qrLabel.printerId'

// localStorage may be unavailable (private mode, blocked site data): remembering the printer is a convenience only.
const rememberedPrinterId = () => {
  try {
    return Number(window.localStorage.getItem(LAST_PRINTER_KEY)) || 0
  } catch (error) {
    return 0
  }
}

const rememberPrinterId = (id) => {
  try {
    window.localStorage.setItem(LAST_PRINTER_KEY, String(id))
  } catch (error) {
    // Nothing to do: next time the user chooses again.
  }
}

// Historia 226 (CU6 step 1; S124-S126, S140): the active printers offered by the QR dialog and the chosen one. With a
// single active printer it comes chosen; with several, the last one chosen in this browser if it is still active.
const useActiveLabelPrinters = (enabled) => {
  const [printers, setPrinters] = useState([])
  const [printerId, setPrinterId] = useState('')
  const [defaultId, setDefaultId] = useState('')

  const reload = useCallback(async () => {
    let all = []
    try {
      all = await listPrinters()
    } catch (error) {
      all = []
    }
    let preferred = ''
    try {
      preferred = (await getDefaultPrinter())?.printerId || ''
    } catch (error) {
      preferred = ''
    }
    const active = all.filter((printer) => printer.active)
    setPrinters(active)
    setDefaultId(preferred)
    setPrinterId((current) => {
      const ids = active.map((printer) => printer.id)
      if (ids.includes(current)) return current
      if (ids.includes(preferred)) return preferred
      if (ids.length === 1) return ids[0]
      const remembered = rememberedPrinterId()
      return ids.includes(remembered) ? remembered : ''
    })
  }, [])

  useEffect(() => {
    if (enabled) reload()
  }, [enabled, reload])

  const choose = useCallback((id) => {
    setPrinterId(id)
    rememberPrinterId(id)
  }, [])

  // E8: "Use as default" stars the printer for this user; the dialog preselects it next time (if it is active).
  const makeDefault = useCallback(async (id) => {
    try {
      await setDefaultPrinter(id)
      setDefaultId(id)
    } catch (error) {
      // The star stays where it was; nothing else depends on it.
    }
  }, [])

  // The outcome of a print is the printer's newest check (RN31): reflect it in the selector without reloading.
  const updatePrinter = useCallback((id, changes) => {
    setPrinters((current) => current.map((printer) => (printer.id === id ? { ...printer, ...changes } : printer)))
  }, [])

  const chosen = printers.find((printer) => printer.id === printerId) || {}

  return { printers, printerId, defaultId, chosen, choose, makeDefault, reload, updatePrinter }
}

export default useActiveLabelPrinters
